/**
 * QuickStart information for Weave product.
 */

import {Button} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {ButtonSizes} from '@wandb/weave/components/Button';
import {getProviderNoteComponent} from '@wandb/weave/components/CodeBlock/ProviderNotes';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {
  getCodeSnippetForProvider,
  getEvaluationSnippetForProvider,
  LLMProvider as WeaveProvider,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/common/traceCodeSnippets';
import {Tailwind} from '@wandb/weave/components/Tailwind';
// @todo: Migrate to 'Button' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {ToggleButtonGroup} from '@wandb/weave/components/ToggleButtonGroup';
import copyToClipboard from 'copy-to-clipboard';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useHistory} from 'react-router-dom';
// @todo: Migrate to 'Slider' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Slide, toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {envIsLocal} from '../../../config';
import {
  useProjectFromNameQuery,
  useUpsertModelMutation,
  useViewerApiKeyQuery,
} from '../../../generated/graphql';
import * as Track from '../../../services/analytics';
import {QuickStartAnalyticsOrigin} from '../../../services/analytics/quickStartEvents';
import {doNotRetryContext, propagateErrorsContext} from '../../../util/errors';
import {AccessOption} from '../../../util/permissions';
import {useRampFlagDisableCopyApiKey} from '../../../util/rampFeatureFlags';
import {weaveEvalColab, weaveTraceColab} from '../../../util/urls';
import {useGetInferenceUsageDetails} from '../../../util/usage/inference';
import {CodeSnippet} from '../../CodeSnippet';
import {GenerateApiKeyButton} from '../../GenerateApiKeyButton';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../../Search/SearchNav/types';
import * as S from '../QuickStart.styles';
import {Product} from '../QuickStartProductSelect';
// Define constants
const QUICKSTART_PROJECT_NAME = 'quickstart_playground';

export const Code = styled.code`
  font-family: 'Source Code Pro';
`;
Code.displayName = 'S.Code';

// LLM Provider display names (for UI)
type LLMProvider = 'W&B Inference' | 'OpenAI' | 'Anthropic';

// Map display names to weave provider keys
const DISPLAY_NAME_TO_PROVIDER: Record<LLMProvider, WeaveProvider> = {
  OpenAI: 'openai',
  Anthropic: 'anthropic',
  'W&B Inference': 'wandb-inference',
};

type QuickStartWeaveProps = {
  entityName: string;
  projectName: string;
  analyticsOrigin: QuickStartAnalyticsOrigin;
  maxWidth: number;
  onVariantActivated: (product: Product, variant: string) => void;
};

export const QuickStartWeave = ({
  entityName,
  projectName,
  analyticsOrigin,
  maxWidth,
  onVariantActivated,
}: QuickStartWeaveProps) => {
  // Get inference enablement status
  const {selectedAccount} = useContext(AccountSelectorContext);
  const isPersonalAccount =
    selectedAccount?.accountType === AccountType.Personal;
  const isNonLocalPersonalAccount = !envIsLocal && isPersonalAccount;

  // Get inference usage details to check balance and pay-as-you-go status
  const inferenceUsageDetails = useGetInferenceUsageDetails(selectedAccount);
  const {isUsagePaid, limits, usage} = inferenceUsageDetails;

  // Check if user has token balance remaining
  const hasTokenBalance =
    limits.inferenceFreeLimit && usage
      ? limits.inferenceFreeLimit - usage > 0
      : false;

  // User can access W&B Inference if:
  // 1. They have token balance remaining, OR
  // 2. They have pay-as-you-go enabled (isUsagePaid)
  // 3. They are using a non-hidden personal entity account on multi-tenant
  const isInferenceEnabled =
    hasTokenBalance || isUsagePaid || isNonLocalPersonalAccount;

  // Set default provider based on inference availability
  const defaultProvider: LLMProvider = isInferenceEnabled
    ? 'W&B Inference'
    : 'OpenAI';
  const [selectedProvider, setSelectedProvider] =
    useState<LLMProvider>(defaultProvider);

  // Options for ToggleButtonGroup - filter out W&B Inference if not enabled
  const providerOptions = [
    ...(isInferenceEnabled ? [{value: 'W&B Inference' as LLMProvider}] : []),
    {value: 'OpenAI' as LLMProvider},
    {value: 'Anthropic' as LLMProvider},
  ];

  const disableCopyApiKey = useRampFlagDisableCopyApiKey();
  const viewerApiKey = useViewerApiKeyQuery();
  const apiKey = viewerApiKey.data?.viewer?.apiKey;
  const hasApiKey = !viewerApiKey.loading && !!apiKey;
  const history = useHistory();

  // Track quickstart viewed on component mount
  useEffect(() => {
    Track.quickStartViewed({
      projectName,
      entityName,
      location: analyticsOrigin,
      quickstartType: 'weave',
      quickstartVariant: 'weave-code-with-colab',
    });
  }, [projectName, entityName, analyticsOrigin]);

  // Playground logic
  const targetProject =
    projectName && projectName !== '' ? projectName : QUICKSTART_PROJECT_NAME;
  const [upsertProjectMutation] = useUpsertModelMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
  });
  const playgroundProjectQuery = useProjectFromNameQuery({
    variables: {
      projectName: targetProject,
      entityName,
    },
  });

  const projectExists = useMemo(() => {
    if (playgroundProjectQuery.loading) {
      return {loading: true, projectExists: undefined};
    } else if (playgroundProjectQuery.data?.project?.id) {
      return {loading: false, projectExists: true};
    } else {
      return {loading: false, projectExists: false};
    }
  }, [playgroundProjectQuery.data, playgroundProjectQuery.loading]);

  const onApiKeyCopyClick = useCallback(() => {
    copyToClipboard(apiKey || '');
    toast('Copied to clipboard', {
      transition: Slide,
    });

    Track.copiedApiKey({
      projectName,
      entityName,
      location: analyticsOrigin,
      frameworkName: 'weave',
    });
  }, [analyticsOrigin, apiKey, entityName, projectName]);

  const generateApiKeyTrackingEvent = () => {
    Track.generatedApiKeyFromQuickStart({
      projectName,
      entityName,
      location: analyticsOrigin,
      frameworkName: 'weave',
    });
  };

  const projectNameStr = projectName === '' ? 'intro-example' : projectName;

  // Get code snippet based on selected provider using imported weave functions
  const weaveProvider = DISPLAY_NAME_TO_PROVIDER[selectedProvider];
  const codeSnippet = getCodeSnippetForProvider(
    weaveProvider,
    entityName,
    projectNameStr
  );
  const evaluationSnippet = getEvaluationSnippetForProvider(
    weaveProvider,
    entityName,
    projectNameStr
  );
  const traceColabLink = weaveTraceColab();
  const evalColabLink = weaveEvalColab();

  return (
    <>
      <S.QuickStartHeader>
        <S.QuickStartHeaderText>
          {' '}
          Quickstart: Creating your first trace in W&B Weave
        </S.QuickStartHeaderText>
        <p>Weave is a toolkit for developing AI-powered applications.</p>
        <div>
          You can use Weave to:
          <ul>
            <li>Log and debug language model inputs, outputs, and traces</li>
            <li>
              Build rigorous, apples-to-apples evaluations for language model
              use cases
            </li>
            <li>
              Organize all the data generated across the LLM workflow, from
              experimentation to evaluation to production
            </li>
          </ul>
          Start by logging a trace—either from the playground or right from your
          code. Visit{' '}
          <TargetBlank href="https://wandb.me/weave">
            our documentation
          </TargetBlank>{' '}
          to learn more.
        </div>
      </S.QuickStartHeader>

      <div>
        <S.QuickStartOrderedList>
          <S.QuickStartListItem>Set up the Weave library</S.QuickStartListItem>
          <S.QuickStartStepContent>
            <p>
              Install the CLI and Python library for interacting with Weave and
              Wandb.
            </p>
            <CodeSnippet
              codeSnippet="pip install wandb weave"
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave',
              }}
            />
            <p>Next, login to wandb and paste your API key when prompted.</p>
            <CodeSnippet
              codeSnippet="wandb login"
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave',
              }}
            />
            {!disableCopyApiKey && hasApiKey ? (
              <S.UserApiKeyRow analyticsOrigin={analyticsOrigin}>
                <S.UserApiKeyLabel>
                  <S.QuickStartIcon>
                    <Icon name="key-admin" />
                  </S.QuickStartIcon>
                  <p className="mr-auto">
                    <b>Your API key</b> for logging in to the wandb library.
                  </p>
                </S.UserApiKeyLabel>
                <S.UserApiKeyBox onClick={onApiKeyCopyClick}>
                  <p>{apiKey}</p>
                  <S.QuickStartIcon>
                    <Icon name="copy" />
                  </S.QuickStartIcon>
                </S.UserApiKeyBox>
              </S.UserApiKeyRow>
            ) : (
              <Tailwind>
                <S.UserApiKeyRow
                  analyticsOrigin={analyticsOrigin}
                  className="flex w-full justify-between">
                  <S.UserApiKeyLabel>
                    <S.QuickStartIcon>
                      <Icon name="key-admin" />
                    </S.QuickStartIcon>
                    <p>
                      <b>Generate your API key.</b> Use it to log in to the
                      wandb library.
                    </p>
                  </S.UserApiKeyLabel>
                  <div className="font-semibold hover:cursor-pointer">
                    <GenerateApiKeyButton
                      asText
                      trackingEvent={generateApiKeyTrackingEvent}
                    />
                  </div>
                </S.UserApiKeyRow>
              </Tailwind>
            )}
            <p>
              You can also set your API key with the following environment
              variable.
            </p>
            <CodeSnippet
              codeSnippet={`import os\nos.environ['WANDB_API_KEY'] = '${
                !disableCopyApiKey && hasApiKey ? apiKey : 'your_api_key'
              }'`}
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave',
              }}
            />
          </S.QuickStartStepContent>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
            }}>
            <S.QuickStartListItem
              style={{marginBottom: '0', marginRight: '8px'}}>
              Log a trace with code or
            </S.QuickStartListItem>
            <TargetBlank href={traceColabLink}>
              <S.ColabButton
                variant="contained"
                color="default"
                size="small"
                onClick={() => {
                  Track.clickedOpenInColab({
                    projectName,
                    entityName,
                    location: analyticsOrigin,
                    frameworkName: 'Weave - None Specified',
                    colabLink: traceColabLink,
                  });
                }}
                startIcon={
                  <S.QuickStartIcon>
                    <Icon name="logo-colab" />
                  </S.QuickStartIcon>
                }>
                Open in Colab
              </S.ColabButton>
            </TargetBlank>
          </div>
          <S.QuickStartStepContent>
            <p>
              Start tracking inputs and outputs of functions by decorating them
              with <Code>weave.op</Code>.<br />
              Run this sample code to see the new trace.
            </p>

            <div style={{marginBottom: '8px'}}>
              <ToggleButtonGroup
                options={providerOptions}
                value={selectedProvider}
                size={ButtonSizes.Small}
                onValueChange={(provider: string) => {
                  setSelectedProvider(provider as LLMProvider);
                }}
              />
            </div>
            {getProviderNoteComponent(weaveProvider)}
            <CodeSnippet
              showExpandCollapse={false}
              codeSnippet={codeSnippet}
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave',
              }}
            />
          </S.QuickStartStepContent>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
            }}>
            <S.QuickStartListItem
              style={{marginBottom: '0', marginRight: '8px'}}>
              Running your first evaluation or
            </S.QuickStartListItem>
            <TargetBlank href={evalColabLink}>
              <S.ColabButton
                variant="contained"
                color="default"
                size="small"
                onClick={() => {
                  Track.clickedOpenInColab({
                    projectName,
                    entityName,
                    location: analyticsOrigin,
                    frameworkName: 'Weave - Evaluation',
                    colabLink: evalColabLink,
                  });
                }}
                startIcon={
                  <S.QuickStartIcon>
                    <Icon name="logo-colab" />
                  </S.QuickStartIcon>
                }>
                Open in Colab
              </S.ColabButton>
            </TargetBlank>
          </div>
          <S.QuickStartStepContent>
            <p>
              Evaluate a simple JSON QA task with Weave evaluators. Pick your
              provider below and run the snippet to log results.
            </p>
            <div style={{marginBottom: '8px'}}>
              <ToggleButtonGroup
                options={providerOptions}
                value={selectedProvider}
                size={ButtonSizes.Small}
                onValueChange={(provider: string) => {
                  setSelectedProvider(provider as LLMProvider);
                }}
              />
            </div>
            {getProviderNoteComponent(weaveProvider)}
            <CodeSnippet
              showExpandCollapse={true}
              codeSnippet={evaluationSnippet}
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave-evaluation',
              }}
            />
          </S.QuickStartStepContent>
          <S.QuickStartListItem>
            Get started with Playground
          </S.QuickStartListItem>
          <S.QuickStartStepContent>
            <p>
              You can interactively develop, review, and test their prompts
              using our LLM playground which supports all major model providers.
            </p>
            <Tailwind className="h-screen">
              <div className="flex w-[800px] content-start justify-between">
                <div className="my-6 flex-initial">
                  <Button
                    isDisabled={projectExists.loading}
                    size="md"
                    endIcon="chevron-next"
                    onPress={async () => {
                      try {
                        Track.clickedWeavePlayground({
                          projectName: targetProject,
                          entityName,
                          location: analyticsOrigin,
                        });
                        if (!projectExists.projectExists) {
                          await upsertProjectMutation({
                            variables: {
                              description: QUICKSTART_PROJECT_NAME,
                              access: AccessOption.Private,
                              name: targetProject,
                              entityName,
                            },
                          });
                        }
                        history.push(
                          `/${entityName}/${targetProject}/weave/playground`
                        );
                      } catch (e) {
                        toast.error(
                          'Failed to create project: Please contact team admin to create a project.'
                        );
                        console.error(e);
                      }
                    }}>
                    Try playground
                  </Button>
                </div>
              </div>
            </Tailwind>
          </S.QuickStartStepContent>
        </S.QuickStartOrderedList>
      </div>
    </>
  );
};
